import "@popperjs/core/dist/umd/popper.min.js";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.min.js";
import "@popperjs/core/dist/umd/popper.min.js";
import "./src/styles/global/global.css";

import { getConsent } from "./src/utils/cookieConsent";
import { loadGtag, trackPageView } from "./src/utils/analytics";

export { wrapRootElement } from "./gatsby-ssr";

// True only during a POP (browser back/forward) navigation.
// popstate fires exclusively for POP — link clicks (PUSH) never trigger it.
let isPopNavigation = false;
// Scroll position retrieved from Gatsby's saved state for the POP target page.
let savedScrollPosition: [number, number] | null = null;

export const onClientEntry = () => {
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }
  window.addEventListener("popstate", () => {
    isPopNavigation = true;
  });

  // Returning visitors who already granted analytics consent never see the
  // banner again, so gtag must be loaded here or their visits go untracked.
  const consent = getConsent();
  if (consent?.analytics) {
    loadGtag();
    if (typeof (window as any).gtag === "function") {
      (window as any).gtag("consent", "update", {
        analytics_storage: "granted",
        ad_storage: consent.marketing ? "granted" : "denied",
      });
    }
  }
};

export const onRouteUpdate = ({
  location,
  prevLocation,
}: {
  location: { hash?: string };
  prevLocation: object | null;
}) => {
  // The initial page_view is sent by gtag's config call when it loads; only
  // in-app navigations (prevLocation set) need a manual page_view. Deferred
  // (same 32ms as gatsby-plugin-google-gtag) so document.title has been
  // updated for the new page before it is reported.
  if (prevLocation) {
    setTimeout(trackPageView, 32);
  }

  if (location.hash) {
    // Defer scroll until after React has finished rendering the new page's DOM.
    // 300ms gives React enough time to mount the new page's component tree before
    // querying the element — 0ms fires too early and misses freshly navigated pages.
    setTimeout(() => {
      const el = document.getElementById(location.hash!.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 300);
    isPopNavigation = false;
    savedScrollPosition = null;
    return;
  }

  // Bootstrap 5 sets `scroll-behavior: smooth` on :root, which makes any
  // window.scrollTo() an animated scroll that can be interrupted mid-animation.
  // behavior:"instant" inside requestAnimationFrame bypasses it unconditionally —
  // this applies to both scroll-to-top (PUSH) and scroll-restore (POP).
  const [x, y] =
    isPopNavigation && savedScrollPosition ? savedScrollPosition : [0, 0];
  requestAnimationFrame(() => {
    window.scrollTo({ top: y, left: x, behavior: "instant" });
  });

  isPopNavigation = false;
  savedScrollPosition = null;
};

export const shouldUpdateScroll = ({
  routerProps: { location },
  getSavedScrollPosition,
}: {
  routerProps: { location: { hash?: string } };
  getSavedScrollPosition: (location: object) => [number, number] | null;
}) => {
  // Hash scrolling is handled manually in onRouteUpdate after DOM is ready.
  if (location.hash) {
    return false;
  }

  if (isPopNavigation) {
    // Capture the saved position so onRouteUpdate can restore it with
    // behavior:"instant" — preventing Bootstrap's smooth-scroll interference.
    savedScrollPosition = getSavedScrollPosition(location);
  }

  // Disable Gatsby's built-in scroll; onRouteUpdate owns all scroll logic.
  return false;
};
